import { getBackendImageUrl } from "./utils";

export interface CollectionArtwork {
  id: string;
  title: string;
  image: string | null;
  thumbnailUrl: string | null;
  format: string;
  isPremium?: boolean;
  isFree?: boolean;
  fileSize?: number | null;
  collectionSlug?: string | null;
}

export interface Collection {
  id: string;
  slug: string;
  title: string;
  description: string;
  tags: string[];
  category?: string | null;
  cover: string | null;
  previews: string[];
  assetsCount: number;
  isFeatured?: boolean;
  isPremium?: boolean;
  createdAt?: string | null;
  updatedAt?: string | null;
  artworks?: CollectionArtwork[];
}

interface CollectionsResponse {
  data?: Collection[];
  meta?: {
    count: number;
    total: number;
    limit: number;
    page: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

export interface CollectionsPaginated {
  collections: Collection[];
  meta: {
    total: number;
    page: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:4001/api";

async function requestCollections(path: string): Promise<Collection | Collection[] | null> {
  try {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      // Coleções mudam pouco, mas queremos atualizar rapidamente depois de novos uploads.
      next: { revalidate: 60 }
    });

    if (!response.ok) {
      console.error(`Falha ao carregar${path.startsWith("/collections") ? " lista" : " coleção"}:`, response.status);
      return null;
    }

    const payload = (await response.json()) as CollectionsResponse | Collection;

    if (Array.isArray((payload as CollectionsResponse).data)) {
      return (payload as CollectionsResponse).data ?? [];
    }

    return payload as Collection;
  } catch (error) {
    console.error(`Erro ao requisitar ${path}:`, error);
    return null;
  }
}

async function requestCollectionsPaginated(path: string): Promise<CollectionsResponse | null> {
  try {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      next: { revalidate: 60 }
    });

    if (!response.ok) {
      console.error(`Falha ao carregar lista de coleções:`, response.status);
      return null;
    }

    return (await response.json()) as CollectionsResponse;
  } catch (error) {
    console.error(`Erro ao requisitar ${path}:`, error);
    return null;
  }
}

export async function fetchCollections(page: number = 1, limit: number = 24, search: string = ''): Promise<CollectionsPaginated> {
  const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
  const response = await requestCollectionsPaginated(`/collections?page=${page}&limit=${limit}${searchParam}`);

  if (response && response.data) {
    const collections = response.data.map((col) => ({
      ...col,
      cover: getBackendImageUrl(col.cover) ?? col.cover,
      previews: (col.previews || []).map((p) => getBackendImageUrl(p) ?? p),
      artworks: (col.artworks || []).map((a) => ({
        ...a,
        image: getBackendImageUrl(a.image) ?? a.image,
        thumbnailUrl: getBackendImageUrl(a.thumbnailUrl) ?? a.thumbnailUrl
      }))
    }));

    return {
      collections,
      meta: {
        total: response.meta?.total ?? collections.length,
        page: response.meta?.page ?? 1,
        totalPages: response.meta?.totalPages ?? 1,
        hasNextPage: response.meta?.hasNextPage ?? false,
        hasPrevPage: response.meta?.hasPrevPage ?? false
      }
    };
  }

  return {
    collections: [],
    meta: {
      total: 0,
      page: 1,
      totalPages: 1,
      hasNextPage: false,
      hasPrevPage: false
    }
  };
}

export async function fetchCollectionBySlug(slug: string): Promise<Collection | null> {
  const collection = await requestCollections(`/collections/${slug}`);

  if (collection && !Array.isArray(collection)) {
    return collection;
  }

  return null;
}
